import { useEffect } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { DocButton } from "@/components/ui/doc-button";
import { useMe } from "@/lib/hooks/use-clients";
import { useT } from "@/lib/i18n/use-t";
import { hasPermission, type Permission } from "@/lib/permissions";
import { ApiKeyWrapper } from "./api-key-wrapper";
import { T } from "./page.i18n";

export default function ApiKeysPage() {
  const navigate = useNavigate();
  const t = useT(T);
  const { me, isLoading } = useMe();
  const isQuantfolio = me?.tenantSlug === "quantfolio";

  // QL-443 — the page mints public API credentials; developer permission
  // required (the backend enforces the same on /internal/security/jwt-token).
  const userPermissions = (me?.permissions || ["advisor"]) as Permission[];
  const canAccess = hasPermission(userPermissions, "api-keys");

  useEffect(() => {
    if (!isLoading && !canAccess) {
      navigate("/", { replace: true });
    }
  }, [isLoading, canAccess, navigate]);

  if (isLoading || !canAccess) return null;

  return (
    <div className="space-y-8">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.apiKeys }]}
        title={t.apiKeys}
        description={t.pageDescription}
        actions={
          <>
            <Button variant="outline" size="sm" className="gap-2" asChild>
              <a
                href={`${process.env.BUN_PUBLIC_API_URL}/doc`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.apiDocs}
              </a>
            </Button>
            {isQuantfolio && (
              <Button variant="outline" size="sm" className="gap-2" asChild>
                <a
                  href={`${process.env.BUN_PUBLIC_API_URL}/admin-doc`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.adminApiDocs}
                </a>
              </Button>
            )}
            <DocButton docSlug="api-keys" variant="outline" size="sm">
              {t.documentation}
            </DocButton>
          </>
        }
      />

      <ApiKeyWrapper />
    </div>
  );
}
